import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="city-autocomplete"
export default class extends Controller {
    static targets = [
        "dropdown", "selector", "selected", "search", "list",
        "spinner", "loadingState", "emptyState", "hiddenField"
    ]

    static values = {
        initialValue: String
    }

    connect() {
        this.cities = []
        this.searchTimeout = null
        this.debounceDelay = 300

        // --- INITIAL VALUE ---------------------------------------------
        if (this.hasInitialValueValue && this.initialValueValue) {
            this.selectedTarget.textContent = this.initialValueValue
            this.hiddenFieldTarget.value = this.initialValueValue
        }

        document.addEventListener("click", this.closeOnOutsideClick)
        document.addEventListener("keydown", this.keyboardNavigation)
        window.addEventListener("resize", this.repositionDropdown)
        window.addEventListener("scroll", this.repositionDropdown, true)
    }

    disconnect() {
        document.removeEventListener("click", this.closeOnOutsideClick)
        document.removeEventListener("keydown", this.keyboardNavigation)
        window.removeEventListener("resize", this.repositionDropdown)
        window.removeEventListener("scroll", this.repositionDropdown, true)
    }

    // --- DROPDOWN -------------------------------------------------------

    toggleDropdown(event) {
        event.stopPropagation()
        this.dropdownTarget.classList.toggle("hidden")

        // Rotate the chevron icon (supports both svg and i elements)
        const chevron = this.selectorTarget.querySelector("svg") || this.selectorTarget.querySelector("i")
        if (chevron) {
            chevron.style.transform = this.dropdownTarget.classList.contains("hidden") ? "rotate(0deg)" : "rotate(180deg)"
        }

        if (!this.dropdownTarget.classList.contains("hidden")) {
            this.positionDropdown()
            this.searchTarget.focus()
        }
    }

    // --- POSITIONING (Responsive dropdown: opens up or down based on available space) ---

    positionDropdown() {
        const selectorRect = this.selectorTarget.getBoundingClientRect()
        const dropdownHeight = this.dropdownTarget.offsetHeight || 320 // max-h-80 = 320px
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - selectorRect.bottom
        const spaceAbove = selectorRect.top

        // Reset positioning classes
        this.dropdownTarget.classList.remove("bottom-full", "top-full", "mb-1", "mt-1")

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            // Not enough space below, open upwards
            this.dropdownTarget.classList.add("bottom-full", "mb-1")
            this.dropdownTarget.style.top = "auto"
        } else {
            // Enough space below or more space below than above, open downwards
            this.dropdownTarget.classList.add("mt-1")
            this.dropdownTarget.style.top = ""
        }
    }

    repositionDropdown = () => {
        if (!this.dropdownTarget.classList.contains("hidden")) {
            this.positionDropdown()
        }
    }

    closeOnOutsideClick = (event) => {
        if (!this.element.contains(event.target)) this.closeDropdown()
    }

    closeDropdown() {
        if (this.dropdownTarget.classList.contains("hidden")) return

        this.dropdownTarget.classList.add("hidden")
        
        // Reset the chevron icon rotation (supports both svg and i elements)
        const chevron = this.selectorTarget.querySelector("svg") || this.selectorTarget.querySelector("i")
        if (chevron) {
            chevron.style.transform = "rotate(0deg)"
        }
        
        this.searchTarget.value = ""
        this.spinnerTarget.classList.add("hidden")
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
    }

    // --- SEARCHING ------------------------------------------------------

    search() {
        const query = this.searchTarget.value.trim()

        if (!query || query.length < 2) {
            this.listTarget.innerHTML = ""
            this.emptyStateTarget.classList.add("hidden")
            return
        }

        this.spinnerTarget.classList.remove("hidden")

        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => this.fetchCities(query), this.debounceDelay)
    }

    async fetchCities(query) {
        this.showLoadingState()

        try {
            const response = await fetch(`/search/cities?q=${encodeURIComponent(query)}`, {
                headers: { "Accept": "application/json" }
            })

            const json = await response.json()
            this.cities = json

            this.renderCityList()
        } catch (e) {
            console.error("Failed fetching cities:", e)
            this.cities = []
            this.renderCityList()
        }
    }

    // --- RENDERING ------------------------------------------------------

    renderCityList() {
        this.listTarget.innerHTML = ""

        if (this.cities.length === 0) {
            this.emptyStateTarget.classList.remove("hidden")
            this.loadingStateTarget.classList.add("hidden")
            this.spinnerTarget.classList.add("hidden")
            return
        }

        this.emptyStateTarget.classList.add("hidden")
        this.loadingStateTarget.classList.add("hidden")
        this.spinnerTarget.classList.add("hidden")

        this.cities.forEach(city => {
            const li = document.createElement("li")
            li.className = "city-item"

            li.innerHTML = `
                <button type="button"
                    class="w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50"
                    data-city='${JSON.stringify(city).replace(/'/g, "&#39;")}'
                >
                    <span class="font-medium block">${city.city}</span>
                    <span class="text-gray-500 text-sm">${city.country}</span>
                </button>
            `

            li.querySelector("button").addEventListener("click", (e) => {
                const cityData = JSON.parse(
                    e.currentTarget.dataset.city.replace(/&#39;/g, "'")
                )
                this.selectCity(cityData)
            })

            this.listTarget.appendChild(li)
        })
    }

    showLoadingState() {
        this.listTarget.innerHTML = ""
        this.emptyStateTarget.classList.add("hidden")
        this.loadingStateTarget.classList.remove("hidden")
    }

    // --- SELECTION ------------------------------------------------------

    selectCity(city) {
        // display_name is returned from Rails controller
        const display = city.display_name

        this.selectedTarget.textContent = display
        this.hiddenFieldTarget.value = display

        this.closeDropdown()
    }

    // --- KEYBOARD NAVIGATION -------------------------------------------

    keydown(event) {
        this.keyboardNavigation(event)
    }

    keyboardNavigation = (event) => {
        if (this.dropdownTarget.classList.contains("hidden")) return

        const items = Array.from(this.listTarget.querySelectorAll("button"))
        const focused = document.activeElement
        let index = items.indexOf(focused)

        if (event.key === "ArrowDown") {
            event.preventDefault()
            index = (index + 1) % items.length
            items[index]?.focus()
        }

        if (event.key === "ArrowUp") {
            event.preventDefault()
            index = index <= 0 ? items.length - 1 : index - 1
            items[index]?.focus()
        }

        if (event.key === "Enter" && items.includes(focused)) {
            event.preventDefault()
            focused.click()
        }

        if (event.key === "Escape") {
            this.closeDropdown()
        }
    }
}
